import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import ArrowBack from '@mui/icons-material/ArrowBack';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getHostingDomainAvailabilityQueryKey,
  getHostingSitesV2QueryKey,
  getSitesListV2QueryKey,
  getUserHostingQueryKey,
  useDomains,
  useHostingDomainAvailability,
  useHostingSiteAddV2,
  useSitesInfo,
  useSitesSecuritySslStatus,
  useSitesSso,
} from '@newfold/huapi-js';
import {
  HostingSiteAddV2Body,
  HostingSitesV2200ItemsItem,
  SitesSsoBounceName,
} from '@newfold/huapi-js/src/index.schemas';

import useAlertStore from '~/components/Alerts/alertsStore';
import { invalidateMultipleQueries } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import { HOSTING_MAX_FETCH_LIMIT } from '~/components/TenantProvider/utils/constants';
import useAccount from '~/hooks/useAccount';
import useAMRevamp from '~/hooks/useAMRevamp';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import useHostingNavigate from '~/hooks/useHostingNavigate';
import LaunchMigrationModal from '~/scenes/AddSetupSiteFlows/components/LaunchMigrationModal';
import TransferOptions from '~/scenes/AddSetupSiteFlows/components/TransferOptions';
import TransferSiteV2 from '~/scenes/AddSetupSiteFlows/components/TransferSiteV2';
import { useAddSiteContext } from '~/scenes/AddSetupSiteFlows/context/AddSiteContext';
import { handleDomainFlow } from '~/scenes/Site/scenes/Domains/utils/helpers';
import WordPressIcon from '~/scenes/Sites/assets/icons/WordPressIcon';
import { canonicalizeUrl } from '~/utils/site/helpers';

import ErrorModal from '../../../AddSetupSiteFlows/components/ErrorModal';
import MultiStepCard from '../../../AddSetupSiteFlows/components/MultiStepCard';
import UpsellResourcesBanner from '../../components/UpsellResourcesBanner';
import SiteInstalledModal from './components/SiteInstalledModal';
import ChooseResources from './scenes/ChooseResources';
import ChooseSite from './scenes/ChooseSite';
import ChooseTitle from './scenes/ChooseTitle';
import ChooseType from './scenes/ChooseType';
import ConnectDomainWithCTB from './scenes/ConnectDomainWithCTB';

export type FormDataType = HostingSiteAddV2Body & {
  autoGeneration?: boolean;
};

const CloudAddSiteWithCTB = ({
  siteList = undefined,
  accountProdInstaId,
}: {
  siteList?: HostingSitesV2200ItemsItem[];
  accountProdInstaId: string;
}) => {
  const { isAMRevamp } = useAMRevamp();
  const [, { generateAlert }] = useAlertStore();
  const { t } = useTranslation(['addSites', 'domains', 'errors']);
  const { hostingId: hostingIdParam } = useParams();
  const { id: hostingId } = useAccount();
  const navigate = useNavigate();
  const hostingNavigate = useHostingNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { handleErrors } = useErrorHandler();
  const { brandFromJWT: brand } = useBrandInfo();

  const [newSiteId, setNewSiteId] = useState();
  const [changeLoading, setChangeLoading] = useState(false);
  const [openSiteInstalledModal, setOpenSiteInstalledModal] = useState(false);
  const [existingSiteInfo, setExistingSiteInfo] =
    useState<HostingSitesV2200ItemsItem>({});
  const [openLaunchModal, setOpenLaunchModal] = useState(false);
  const [sslStatusRefetchCount, setSslStatusRefetchCount] = useState(0);
  const isTransferPage = location?.pathname === '/sites/add/transfer';

  const hostingSitesQueryKey = getHostingSitesV2QueryKey(hostingId);
  const sitesListQueryKey = getSitesListV2QueryKey();
  const queryKeys = useMemo(
    () => [hostingSitesQueryKey, sitesListQueryKey],
    [hostingSitesQueryKey, sitesListQueryKey],
  );

  const { formData, setFormData, clearFormData, optionSelected } =
    useAddSiteContext();

  const methods = useForm({
    defaultValues: { ...formData, subtype: 'wordpress' },
  });

  const hookOptions = {
    query: {
      refetchOnWindowFocus: false,
      enabled: false,
    },
  };

  const params = {
    bounce_name: SitesSsoBounceName.wpmigration,
  };

  const { refetch: fetchSso } = useSitesSso(
    Number(newSiteId),
    params,
    hookOptions,
  );

  // TODO: replace with hostingDomainsV2
  // Get a list of all domains on this hosting account
  const { data: domainListFull } = useDomains(hostingId, undefined, {
    query: {
      enabled: !!hostingId,
      select: (data) => [...data?.data?.domains],
    },
  });

  const {
    data: domainAvailability,
    refetch: fetchDomainAvailability,
    isLoading: isLoadingDomainAvailability,
    isError: isErrorDomainAvailability,
    error: domainAvailabilityError,
  } = useHostingDomainAvailability(
    hostingId,
    {
      domain: formData.domain!,
    },
    {
      query: {
        enabled: false,
        staleTime: 0, // disable cache
      },
    },
  );

  const launchMigration = () => {
    setChangeLoading(false);
    setOpenLaunchModal(true);
  };

  const retryMigration = (retryFunc: any) => {
    return generateAlert({
      title: t('migrateSiteOptions.onError.title'),
      description: (
        <Trans
          i18nKey="addSites:migrateSiteOptions.onError.description"
          values={{ phoneNumber: '1-888-401-4678' }}
          components={{ Link: <Link href={'tel:+18884014678'}></Link> }}
        />
      ),
      severity: 'error',
      showCloseBtn: true,
      ctaText: t('migrateSiteOptions.onError.ctaText'),
      cta: retryFunc,
      autoHideDuration: 10000,
    });
  };

  const redirectToWPMigrationUrl = async () => {
    const wordpressSsoLoadingPage =
      brand === 'bluehost'
        ? `${process.env.PUBLIC_URL}/loading/wordpress-${brand}.html`
        : `${process.env.PUBLIC_URL}/loading/wordpress.html`;

    // Actually open the window
    var ssoWindow = window.open(wordpressSsoLoadingPage, '_blank');
    setChangeLoading(true);

    const { data, error } = await fetchSso();

    if (error) {
      // close the window
      ssoWindow?.close();
      setChangeLoading(false);
      return retryMigration(() => redirectToWPMigrationUrl());
    }

    const url = data?.data?.sso;

    if (url && ssoWindow) {
      // set url for window
      ssoWindow.location = url;

      setChangeLoading(false);
      setOpenLaunchModal(false);
      hostingNavigate();
      return;
    }

    ssoWindow?.close();

    generateAlert({
      severity: 'error',
      description: t('ssoLink.notFound'),
      showCloseBtn: true,
    });
    setOpenLaunchModal(false);
    setChangeLoading(false);
  };

  const refetchSslStatusMaxAttempts = 6;
  const enableSslStatusRefetch =
    sslStatusRefetchCount > 0 &&
    sslStatusRefetchCount <= refetchSslStatusMaxAttempts;

  // https://jira.newfold.com/browse/HOST-1807
  // Need to check SSL status before allowing the user to SSO into WordPress
  const {
    data: sslData,
    refetch: refetchSitesSslStatus,
    isSuccess: isSuccessSSL,
    isError: isErrorSSL,
  } = useSitesSecuritySslStatus(
    Number(newSiteId),
    { domain_scan: true },
    {
      query: {
        enabled: enableSslStatusRefetch,
        refetchInterval: (query) => {
          const sslStatus = query?.state?.data?.data?.ssl_status;
          if (
            sslStatusRefetchCount <= refetchSslStatusMaxAttempts &&
            sslStatus !== 'active'
          ) {
            return 20000;
          }
          return false;
        },
      },
    },
  );

  // onSuccess - useSitesSecuritySslStatus
  useEffect(() => {
    if (isSuccessSSL) {
      setSslStatusRefetchCount((old) => old + 1);
      if (sslData?.data?.ssl_status === 'active') {
        setSslStatusRefetchCount(refetchSslStatusMaxAttempts + 1);
        launchMigration();
      }
    }
  }, [isSuccessSSL, sslData]);

  // onError - useSitesSecuritySslStatus
  useEffect(() => {
    if (isErrorSSL || sslStatusRefetchCount >= refetchSslStatusMaxAttempts) {
      generateAlert({
        title: t('migrateSiteOptions.sslError.title'),
        description: t('migrateSiteOptions.sslError.description'),
        severity: 'error',
        showCloseBtn: true,
        persist: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorSSL, sslData]);

  const handleSiteInstalled = useCallback(() => {
    if (isTransferPage) return refetchSitesSslStatus();
    setOpenSiteInstalledModal(true);
    setChangeLoading(false);
  }, [isTransferPage, refetchSitesSslStatus]);

  const handleOnClose = () => {
    setExistingSiteInfo({});
  };

  // Watches for changes in the form to keep localStorage in sync
  // Also helpful for form reset
  useEffect(() => {
    const subscription = methods.watch((value) =>
      setFormData({ ...value } as any),
    );
    return () => subscription.unsubscribe();
  }, [methods, methods.watch, setFormData]);

  // Preserve whichever base segment we entered under: `websites` or `package/websites`.
  const baseSegment = location?.pathname?.includes('/package/websites/')
    ? 'package/websites'
    : 'sites';

  const expectedPaths = useMemo(() => {
    if (isAMRevamp) {
      const suffixMap: Record<string, string> = {
        transfer: 'config',
        clone: 'site',
        add: 'title',
      };
      const suffix = optionSelected ? suffixMap[optionSelected] : undefined;

      if (!suffix) {
        return [`/${baseSegment}/add`, `/${hostingId}/${baseSegment}/add`];
      }

      return [
        `/${baseSegment}/add/${suffix}`,
        `/${hostingId}/${baseSegment}/add/${suffix}`,
      ];
    }

    return [`/${baseSegment}/add`];
  }, [baseSegment, hostingId, isAMRevamp, optionSelected]);

  useEffect(() => {
    const hasRequiredFields = formData?.subtype && formData?.title;
    const isOnExpectedPath = expectedPaths.includes(location?.pathname);

    if (hasRequiredFields || isOnExpectedPath) {
      return;
    }

    if (isAMRevamp) {
      if (!optionSelected) {
        return;
      }

      const suffixMap: Record<string, string> = {
        transfer: 'config',
        clone: 'site',
        add: 'title',
      };
      const suffix = suffixMap[optionSelected];

      if (!suffix) {
        return;
      }

      const defaultPath = `/${baseSegment}/add/${suffix}`;
      const hostingPath = `/${hostingId}/${baseSegment}/add/${suffix}`;
      navigate(hostingIdParam ? hostingPath : defaultPath);
      return;
    }

    navigate('');

    // Need to comment out lint rule to ensure function only runs ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchSiteCount = useRef(0);
  const refetchSiteLimit = 30;

  const installingStatuses = [
    'pending_create',
    'create',
    'pending_configure',
    'configure',
  ];

  const installFailedStatuses = useMemo(
    () => ['failed_create', 'failed_configure'],
    [],
  );

  const {
    data: siteInfo,
    refetch: refetchSiteInfo,
    isSuccess: isSuccessSiteInfo,
  } = useSitesInfo(Number(newSiteId), {
    query: {
      enabled: !!newSiteId,
      refetchInterval: (query) => {
        const status = query?.state?.data?.data?.status!;
        const siteInstallSlow = refetchSiteCount.current >= refetchSiteLimit;
        return (installingStatuses.includes(status) &&
          !installFailedStatuses.includes(status) &&
          !siteInstallSlow) ||
          (isTransferPage && status !== 'active')
          ? 5000
          : false;
      },
    },
  });

  // onSuccess - useSitesInfo
  useEffect(() => {
    if (isSuccessSiteInfo) {
      (async () => {
        const siteInstallSlow = refetchSiteCount.current >= refetchSiteLimit;
        const siteStatus = siteInfo?.data?.status;
        if (siteStatus === 'active') {
          const keysToInvalidate = isAMRevamp
            ? [
                ...queryKeys,
                getUserHostingQueryKey({
                  page: 1,
                  limit: HOSTING_MAX_FETCH_LIMIT,
                }),
              ]
            : queryKeys;
          await invalidateMultipleQueries(queryClient, keysToInvalidate);

          handleSiteInstalled();
          return siteInfo;
        }
        // If site is failed to create/configure, redirect to site list page
        // OR
        // If site is NOT active yet, retry up to 30 times in 5 second intervals
        // If site is still NOT active, redirect to site list page
        refetchSiteCount.current += 1;
        if (
          (siteStatus && installFailedStatuses.includes(siteStatus)) ||
          siteInstallSlow
        ) {
          generateAlert({
            title: t('alertCreatingSite.title'),
            description: t('alertCreatingSite.desc'),
            severity: 'info',
            showCloseBtn: true,
          });
          setChangeLoading(false);
          hostingNavigate();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessSiteInfo, siteInfo]);

  const setAlertMessage = (
    variant: string,
    desc: JSX.Element | string,
    persist: boolean = false,
  ) => {
    generateAlert({
      severity: variant,
      description: desc,
      showCloseBtn: true,
      persist,
    });
  };

  const { mutate: createWebsite } = useHostingSiteAddV2({
    mutation: {
      onMutate: () => setChangeLoading(true),
      onSuccess: async (data) => {
        await invalidateMultipleQueries(queryClient, queryKeys);
        if (formData.migration_id) {
          setTimeout(() => {
            hostingNavigate();
          }, 10000);
        } else {
          //@ts-expect-error
          setNewSiteId(data?.data?.site_id as string);
          // will trigger site info call to check site status after 10 seconds
          setTimeout(() => refetchSiteInfo(), 10000);
        }
        clearFormData();
      },
      onError: (error: any) => {
        if (formData?.migration_id) {
          setAlertMessage(
            'error',
            t('addSites:migrateSiteOptions.transferError'),
            true,
          );
        } else if (error?.response?.data?.error === 'urlInUse') {
          // TODO: Get site info from BE error response to avoid looping through site list
          const existingSite =
            siteList &&
            siteList.find((site: HostingSitesV2200ItemsItem) =>
              canonicalizeUrl(site?.url!).endsWith(
                canonicalizeUrl(`://${formData.domain}/${formData.path}`),
              ),
            );
          if (existingSite) {
            setExistingSiteInfo(existingSite);
          } else {
            generateAlert({
              title: t('alert.urlInUseTitle'),
              description: t('alert.urlInUseDesc', {
                path: canonicalizeUrl(`${formData.domain}/${formData.path}`),
              }),
              severity: 'error',
              showCloseBtn: true,
              persist: true,
            });
          }
        } else if (isTransferPage) {
          setChangeLoading(false);
          retryMigration(() =>
            createWebsite({
              hostingId,
              data: {
                ...(!!formData.workers && {
                  workers: formData.workers,
                }),
                ...(!!formData.datacenter && {
                  datacenter: formData.datacenter,
                }),
                ...(!!formData.disk_limit_gib && {
                  disk_limit_gib: Number(formData?.disk_limit_gib),
                }),
                subtype: 'wordpress',
              } as any,
            }),
          );
        } else {
          handleErrors(error);
        }
        setChangeLoading(false);
      },
    },
  });

  const domainAvailableKey = getHostingDomainAvailabilityQueryKey(hostingId, {
    domain: formData.domain!,
  });

  const handleCreateWebsite = (skip_pointer: boolean, data?: any) => {
    const payload = data ?? formData;
    createWebsite({
      hostingId,
      data: {
        title: payload.title,
        admin_email: payload.admin_email,
        admin_user: payload.admin_user,
        datacenter: payload?.datacenter,
        disk_limit_gib: Number(payload.disk_limit_gib),
        workers: Number(payload.workers),
        subtype: payload.subtype,
        domain: payload.domain,
        ...(formData?.enable_multisite && {
          wp_multi_site: {
            enable: formData?.enable_multisite || false,
            canonicalize_aliases: formData?.multisite_type === 'subdirectory',
          },
        }),
        skip_pointer,
        ...(!!payload.clone_site_id && {
          clone_site_id: payload.clone_site_id,
        }),
      },
    });
  };

  const setErrorExistingDomainAlert = (error: any) => {
    const { error: errorType } = error.response.data;

    const errorMessages: { [key: string]: string } = {
      invalidDomain: t('domainInvalid'),
      domainAvailabilityInValidTldError: t('domainInvalid'),
    };

    const errorMessage = errorMessages[errorType] || t('alertError');

    generateAlert({
      severity: 'error',
      description: errorMessage,
      showCloseBtn: true,
      persist: false,
    });
  };

  useEffect(() => {
    if (isErrorDomainAvailability && domainAvailabilityError) {
      setErrorExistingDomainAlert(domainAvailabilityError);
      // Need to remove this else this method setErrorExistingDomainAlert gets called infinitely
      queryClient.removeQueries({ queryKey: domainAvailableKey });
    }
    if (domainAvailability && domainAvailability.data) {
      const { isError, code, skipSFAPI } = handleDomainFlow(
        domainAvailability?.data,
      );
      if (isError) {
        code === 'existingDomainAddError'
          ? generateAlert({
              description: (
                <Trans
                  i18nKey="domains:domainAssignValidations:existingDomainAddError"
                  components={{ strong: <strong /> }}
                ></Trans>
              ),
              severity: 'error',
              showCloseBtn: true,
            })
          : setAlertMessage(
              'error',
              t(`domains:domainAssignValidations.${code}`),
            );
      } else {
        handleCreateWebsite(skipSFAPI, null);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainAvailabilityError, isErrorDomainAvailability, domainAvailability]);

  const onSubmit = (data: any) => {
    if (!formData?.autoGeneration) {
      const isDomainPresentInAnotherSite =
        domainListFull?.some((domain) => domain.domain === formData.domain) ??
        false;
      if (isDomainPresentInAnotherSite) {
        // TODO: update pointer to current site
        generateAlert({
          severity: 'error',
          description: <Trans i18nKey="errors:domainInUse" />,
          showCloseBtn: true,
          persist: true,
        });
      } else {
        // Fetches the domain availability status
        fetchDomainAvailability();
      }
    } else {
      handleCreateWebsite(false, data);
    }
  };

  return (
    <FormProvider {...methods}>
      <Container data-testid="cloud-add-site-ctb-flow">
        <SiteInstalledModal
          open={openSiteInstalledModal}
          newSiteId={newSiteId}
        />
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          margin={2}
        >
          <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          <Typography variant="h1">
            {t('addSites:addSiteOptions.add')}
          </Typography>
          <WordPressIcon style={{ width: '30px', height: '30px' }} />
          <Typography variant="h1">
            {t('addSites:addSiteOptions.wordpress')}
          </Typography>
        </Stack>
        <Stack spacing={2}>
          <MultiStepCard>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Routes>
                <Route
                  index
                  element={
                    <ChooseType
                      formData={formData}
                      changeLoading={changeLoading}
                    />
                  }
                />
                <Route
                  path="site/*"
                  element={
                    <ChooseSite
                      formData={formData}
                      changeLoading={changeLoading}
                    />
                  }
                />
                <Route
                  path="title/*"
                  element={
                    <ChooseTitle
                      formData={formData}
                      changeLoading={changeLoading}
                    />
                  }
                />
                <Route
                  path="connect/*"
                  element={
                    <ConnectDomainWithCTB
                      formData={formData}
                      setFormData={setFormData}
                      changeLoading={changeLoading}
                      isLoadingDomainAvailability={isLoadingDomainAvailability}
                    />
                  }
                />
                <Route
                  path="config/*"
                  element={
                    <ChooseResources
                      formData={formData}
                      createWebsite={createWebsite}
                      changeLoading={changeLoading}
                    />
                  }
                />
                <Route
                  path="transfer/*"
                  element={<TransferSiteV2 changeLoading={changeLoading} />}
                />
              </Routes>
            </form>
          </MultiStepCard>
          {(location?.pathname === '/sites/add/title' ||
            location?.pathname === '/sites/add/config') && (
            <UpsellResourcesBanner
              title={t('resourcesBanner.title')}
              desc={t('resourcesBanner.description')}
              isCloneWP={false}
              accountProdInstaId={accountProdInstaId}
            />
          )}
        </Stack>
      </Container>
      {isTransferPage && <TransferOptions />}
      <ErrorModal
        open={!!existingSiteInfo?.id}
        errorTitle={t('urlTaken')}
        paragraphOne={
          <Trans
            i18nKey="addSites:alreadySiteName"
            values={{
              path: existingSiteInfo?.url,
            }}
          />
        }
        paragraphTwo={
          <Trans
            i18nKey="addSites:delete"
            values={{ name: existingSiteInfo?.name }}
            components={{
              Link: (
                <Link href={`/sites/${existingSiteInfo?.id}/settings`}></Link>
              ),
            }}
          />
        }
        handleOnClose={handleOnClose}
      />
      <LaunchMigrationModal
        open={openLaunchModal}
        isPending={changeLoading}
        handleContinue={redirectToWPMigrationUrl}
      />
    </FormProvider>
  );
};

export default CloudAddSiteWithCTB;
